import React from 'react';

import branding from '../../../../config/branding';
import { Reveal } from '../../../../components/Reveal';
import {
  Section,
  SectionHeading,
  SectionInner,
  SectionLead,
} from '../../styles';
import {
  CheckCell,
  ComparisonTable,
  ComparisonWrapper,
  Features,
  Footnote,
  Grid,
  PlanCard,
  PlanCta,
  PlanHeader,
  PlanName,
  PlanNoteLabel,
  PlanSubtitle,
  PopularTag,
  Price,
  PricePeriod,
  PriceRow,
  Td,
  Th,
  ThLabel,
} from './styles';

// Fonte única: FUNCIONALIDADES.md (raiz do monorepo) -- qualquer mudança
// de preço/capacidade por plano deve ser espelhada dos dois lados,
// nunca inventada aqui.
const APP_SIGNUP_URL = `${branding.webUrl}/new-gym/`;

interface Plan {
  name: string;
  subtitle: string;
  price: string;
  note: string;
  features: string[];
  highlighted?: boolean;
  popular?: boolean;
}

const plans: Plan[] = [
  {
    name: 'Essencial',
    subtitle:
      'Ideal para estúdios e academias pequenas iniciando na gestão digital.',
    price: 'R$ 99',
    note: '',
    features: [
      'Até 40 alunos ativos',
      'Até 40 exercícios customizados',
      'Prescrição e execução de treinos via app',
      'Biblioteca padrão de exercícios',
      'Controle de pagamentos e mensalidades',
      'Dashboard geral e acesso web/mobile',
      'Permissões de equipe',
    ],
  },
  {
    name: 'Crescimento',
    subtitle:
      'Equilíbrio perfeito entre capacidade, engajamento e custo-benefício.',
    price: 'R$ 199',
    note: 'Tudo do Essencial, mais:',
    features: [
      'Até 120 alunos ativos',
      'Até 100 exercícios customizados',
      'Métricas e evolução corporal do aluno',
      'Insígnias e gamificação de alunos',
      'Mural de avisos com notificação push',
      'Totem de impressão de fichas (autoatendimento)',
      'Templates de treino reutilizáveis',
    ],
    highlighted: true,
    popular: true,
  },
  {
    name: 'Performance',
    subtitle:
      'A experiência completa de gestão, inteligência anti-evasão e app exclusivo.',
    price: 'R$ 349',
    note: 'Tudo do Crescimento, mais:',
    features: [
      'Até 400 alunos ativos*',
      'Até 200 exercícios customizados*',
      'Assistente de criação de fichas de treino',
      'Lista de alunos em risco de evasão',
      'Chat direto aluno ↔ academia',
      'App exclusivo da equipe (instrutores/personal)',
      'Ranking mensal entre alunos',
      'Lembrete automático de vencimento + alerta de inadimplência',
      'Relatórios avançados (retenção, engajamento, exportação CSV)',
    ],
  },
];

const comparisonRows: { label: string; values: (boolean | string)[] }[] = [
  {
    label: 'Limite de alunos ativos',
    values: ['Até 40', 'Até 120', 'Até 400*'],
  },
  {
    label: 'Exercícios customizados',
    values: ['Até 40', 'Até 100', 'Até 200*'],
  },
  { label: 'Prescrição e app do aluno', values: [true, true, true] },
  { label: 'Métricas corporais & gamificação', values: [false, true, true] },
  { label: 'Totem de autoatendimento', values: [false, true, true] },
  { label: 'Notificações push nos avisos', values: [false, true, true] },
  {
    label: 'Templates de treino reutilizáveis',
    values: [false, true, true],
  },
  { label: 'Assistente de criação de fichas', values: [false, false, true] },
  { label: 'Alerta anti-evasão de alunos', values: [false, false, true] },
  {
    label: 'App da equipe (staff / instrutores)',
    values: [false, false, true],
  },
  { label: 'Chat direto no aplicativo', values: [false, false, true] },
  {
    label: 'Cobrança & inadimplência automática',
    values: [false, false, true],
  },
  {
    label: 'Relatórios avançados & exportação CSV',
    values: [false, false, true],
  },
];

const Pricing: React.FC = () => {
  return (
    <Section id="precos" alt>
      <SectionInner>
        <SectionHeading>Planos e preços</SectionHeading>
        <SectionLead maxWidth={680}>
          Experimente 15 dias grátis com acesso total à plataforma. Escolha o
          seu plano apenas quando estiver pronto. Sem fidelidade.
        </SectionLead>

        <Grid>
          {plans.map(plan => (
            <Reveal direction="up" key={plan.name}>
              <PlanCard highlighted={plan.highlighted}>
                <div>
                  <PlanHeader>
                    <PlanName>{plan.name}</PlanName>
                    {plan.popular && <PopularTag>Mais popular</PopularTag>}
                  </PlanHeader>
                  <PlanSubtitle highlighted={plan.highlighted}>
                    {plan.subtitle}
                  </PlanSubtitle>
                </div>

                <PriceRow>
                  <Price>{plan.price}</Price>
                  <PricePeriod highlighted={plan.highlighted}>
                    / mês
                  </PricePeriod>
                </PriceRow>

                <div>
                  {!!plan.note && (
                    <PlanNoteLabel highlighted={plan.highlighted}>
                      {plan.note}
                    </PlanNoteLabel>
                  )}
                  <Features highlighted={plan.highlighted}>
                    {plan.features.map(feature => (
                      <li key={feature}>
                        <span>✓</span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </Features>
                </div>

                <PlanCta
                  variant={plan.highlighted ? 'solid' : 'ghost'}
                  size="lg"
                  fullWidth
                  href={APP_SIGNUP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Testar grátis
                </PlanCta>
              </PlanCard>
            </Reveal>
          ))}
        </Grid>

        <Footnote>
          * Precisa de mais limite para alunos, exercícios ou avisos? Pacotes
          adicionais de expansão podem ser contratados diretamente com à nossa
          equipe de suporte.
        </Footnote>

        <Reveal direction="up">
          <ComparisonWrapper>
            <ComparisonTable>
              <thead>
                <tr>
                  <ThLabel>Categoria / recurso</ThLabel>
                  <Th>
                    Essencial
                    <br />
                    <small>R$ 99</small>
                  </Th>
                  <Th highlight>
                    Crescimento
                    <br />
                    <small>R$ 199</small>
                  </Th>
                  <Th>
                    Performance
                    <br />
                    <small>R$ 349</small>
                  </Th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map(row => (
                  <tr key={row.label}>
                    <Td>{row.label}</Td>
                    {row.values.map((value, index) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <CheckCell key={index} checked={value === true}>
                        {typeof value === 'string' ? value : value ? '✓' : '—'}
                      </CheckCell>
                    ))}
                  </tr>
                ))}
              </tbody>
            </ComparisonTable>
          </ComparisonWrapper>
        </Reveal>
      </SectionInner>
    </Section>
  );
};

export { Pricing };
